import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Divider, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import './Contact.css';
import contactHeroBg from '../assets/hotel/1.jpg';

export function Contact() {
  const contactInfo = [
    {
      icon: <PhoneIcon />,
      title: 'Mobile',
      value: '+41 (0) 79 175 20 80',
      link: 'tel:+41791752080'
    },
    {
      icon: <EmailIcon />,
      title: 'E-Mail',
      value: 'info@plannic.ch',
      link: 'mailto:info@plannic.ch'
    },
    {
      icon: <LocationOnIcon />,
      title: 'Adresse',
      value: 'Thurfeldstrasse 17a, 9215 Schönenberg',
      link: 'https://maps.google.com/?q=Thurfeldstrasse+17a+9215+Schönenberg'
    },
    {
      icon: <LocationOnIcon />,
      title: 'Zweite Adresse',
      value: 'Oberhofenstrasse 2, 8370 Sirnach',
      link: 'https://maps.google.com/?q=Oberhofenstrasse+2+8370+Sirnach'
    }
  ];

  const team = [
    {
      name: 'Liridon Jaja',
      position: 'Geschäftsführer',
      mobile: '079 175 20 80',
      email: 'l.jaja@plannic.ch'
    },
    {
      name: 'Muhamed Jonuzi',
      position: 'Architekt',
      mobile: '076 567 79 75',
      email: 'm.jonuzi@plannic.ch'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      scale: 1.03,
      boxShadow: '0 10px 40px rgba(255, 255, 255, 0.1)',
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          backgroundImage:
            `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${contactHeroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          position: 'relative'
        }}>
        <Container maxWidth='lg'>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography
              variant='h1'
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                fontFamily: 'Jura, sans-serif',
                letterSpacing: '0.02em',
                textAlign: 'center',
                mb: 3
              }}>
              KONTAKT
            </Typography>
            <Typography
              variant='h5'
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '800px',
                mx: 'auto',
                textAlign: 'center',
                fontFamily: 'Jura, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '1rem', md: '1.2rem' },
                lineHeight: 1.6
              }}>
              Wir freuen uns auf Ihre Nachricht
            </Typography>
          </motion.div>
        </Container>
      </Box>
      {/* Contact Information Cards */}
      <Container maxWidth='lg' sx={{ py: { xs: 6, md: 10 } }}>
        <motion.div variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant='h3' className='section-title'>
              Kontaktdetails
            </Typography>
          </Box>

          <Box className='contact-cards-grid'>
            {contactInfo.map((info, index) => (
              <motion.div key={index} variants={itemVariants} whileHover='hover'>
                <Card
                  className='contact-card'
                  component={motion.div}
                  variants={cardHoverVariants}
                  sx={{
                    height: '100%',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(info.link, '_blank')}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <motion.div whileHover={{ rotate: 360, scale: 1.2 }} transition={{ duration: 0.5 }}>
                      <IconButton
                        className='contact-icon'
                        sx={{
                          mb: 2
                        }}>
                        {info.icon}
                      </IconButton>
                    </motion.div>
                    <Typography variant='h6' sx={{ mb: 1, color: '#333', fontWeight: 600 }}>
                      {info.title}
                    </Typography>
                    <Typography variant='body2' sx={{ color: '#666' }}>
                      {info.value}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>

        {/* Team Section */}
        <motion.div variants={containerVariants} initial='hidden' whileInView='visible' viewport={{ once: true }} style={{ marginTop: '80px' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant='h3' className='section-title'>
              Ansprechsperson
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent='center'>
            {team.map((member, index) => (
              <Grid item xs={12} md={5} key={index}>
                <motion.div variants={itemVariants} whileHover='hover'>
                  <Card className='team-card' component={motion.div} variants={cardHoverVariants}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                          <Box
                            sx={{
                              width: 70,
                              height: 70,
                              borderRadius: '50%',
                              background: '#f5f5f5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 3,
                              border: '2px solid #e0e0e0'
                            }}>
                            <PersonIcon sx={{ fontSize: 35, color: '#333' }} />
                          </Box>
                        </motion.div>
                        <Box>
                          <Typography variant='h5' sx={{ color: '#333', fontWeight: 600 }}>
                            {member.name}
                          </Typography>
                          <Typography variant='subtitle1' sx={{ color: '#666' }}>
                            {member.position}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ borderColor: '#e0e0e0', my: 3 }} />

                      <Box
                        component='a'
                        href={`tel:${member.mobile.replace(/\s/g, '')}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2,
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: '#000',
                            transform: 'translateX(5px)'
                          }
                        }}>
                        <PhoneIcon sx={{ color: '#666', mr: 2 }} />
                        <Typography variant='body1' sx={{ color: '#333' }}>
                          {member.mobile}
                        </Typography>
                      </Box>

                      <Box
                        component='a'
                        href={`mailto:${member.email}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          color: 'inherit',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: '#000',
                            transform: 'translateX(5px)'
                          }
                        }}>
                        <EmailIcon sx={{ color: '#666', mr: 2 }} />
                        <Typography variant='body1' sx={{ color: '#333' }}>
                          {member.email}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
      {/* Map Section */}
      <Box sx={{ mt: 10, mb: 0 }}>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant='h3' className='section-title'>
              Unsere Standorte
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: { xs: '300px', md: '450px' },
              overflow: 'hidden',
              borderTop: '1px solid #e0e0e0',
              borderBottom: '1px solid #e0e0e0'
            }}>
            <iframe
              src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2695.5!2d9.2!3d47.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDfCsDI0JzAwLjAiTiA5wrAxMicwMC4wIkU!5e1!3m2!1sen!2sch!4v1234567890'
              width='100%'
              height='100%'
              style={{ border: 0 }}
              allowFullScreen=''
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
              title='Plannic Location'></iframe>
          </Box>
        </motion.div>
      </Box>
    </Layout>
  );
}
